
import { useState, useMemo, useCallback } from "react";
import { useCompanies } from "@/hooks/useCompanies";
import { useOptimizedData } from "@/hooks/useOptimizedData";
import { AdvancedSearch, SearchConfig } from "@/components/ui/search-advanced";
import { OptimizedTable, TableColumn } from "@/components/ui/optimized-table";
import { DataExport } from "@/components/ui/data-export";
import { CompanyDialog } from "@/components/companies/CompanyDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { RefreshCw, Plus, Edit, Trash2, Building, Phone, MapPin, Search } from "lucide-react";
import { ViewToggle } from "@/components/shared/ViewToggle";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

const Companies = () => {
  const { data: companies = [], isLoading, error, refetch } = useCompanies();
  const [selectedCompany, setSelectedCompany] = useState<any | undefined>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [view, setView] = useState<'table' | 'cards'>('table');

  // تكوين البحث المتقدم
  const searchConfig: SearchConfig = useMemo(() => ({
    placeholder: "البحث في الشركات...",
    filters: [
      {
        id: 'mobile',
        label: 'رقم الجوال',
        type: 'text',
        icon: Phone
      },
      {
        id: 'address_ar',
        label: 'العنوان',
        type: 'text',
        icon: MapPin
      }
    ]
  }), []);

  // استخدام hook البيانات المحسن
  const {
    data: displayCompanies,
    allData: filteredCompanies,
    searchQuery,
    setSearchQuery,
    filters,
    addFilter,
    removeFilter,
    clearFilters,
    sortConfig,
    changeSort,
    pagination,
    changePage,
    stats
  } = useOptimizedData({
    data: companies,
    searchFields: ['name_ar', 'mobile', 'address_ar'],
    initialPageSize: 20
  });

  // الإشعارات في الوقت الفعلي
  useEffect(() => {
    const channel = supabase
      .channel('companies_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'companies'
        },
        (payload) => {
          console.log('Company change detected:', payload);
          refetch();
          
          switch (payload.eventType) {
            case 'INSERT':
              toast.success('تم إضافة شركة جديدة');
              break;
            case 'UPDATE':
              toast.info('تم تحديث بيانات شركة');
              break;
            case 'DELETE':
              toast.warning('تم حذف شركة');
              break;
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  // تكوين أعمدة الجدول
  const tableColumns: TableColumn<any>[] = useMemo(() => [
    {
      key: 'name_ar',
      title: 'اسم الشركة',
      dataIndex: 'name_ar',
      sortable: true,
      render: (value, record) => (
        <div className="space-y-1">
          <div className="font-medium text-card-foreground">{value}</div>
          {record.name_en && (
            <div className="text-sm text-muted-foreground">{record.name_en}</div>
          )}
        </div>
      )
    },
    {
      key: 'mobile',
      title: 'رقم الجوال',
      dataIndex: 'mobile',
      render: (value) => value || '-'
    },
    {
      key: 'address_ar',
      title: 'العنوان',
      dataIndex: 'address_ar',
      render: (value) => value || '-'
    },
    {
      key: 'created_at',
      title: 'تاريخ الإضافة',
      dataIndex: 'created_at',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString('ar-SA')
    },
    {
      key: 'actions',
      title: 'الإجراءات',
      align: 'center',
      render: (_, record) => (
        <div className="flex gap-2 justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditCompany(record)}
            className="h-8 w-8 p-0 hover:bg-primary-light"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ], []);

  // معالجات الأحداث
  const handleEditCompany = useCallback((company: any) => {
    setSelectedCompany(company);
    setIsDialogOpen(true);
  }, []);

  const handleAddCompany = useCallback(() => {
    setSelectedCompany(undefined);
    setIsDialogOpen(true);
  }, []);

  const handleSearch = useCallback((query: string, searchFilters: any[]) => {
    setSearchQuery(query);
    
    searchFilters.forEach(filter => {
      addFilter(filter.id, filter.value, filter.type);
    });
  }, [setSearchQuery, addFilter]);

  const searchFilters = useMemo(() => 
    filters.map(filter => {
      const config = searchConfig.filters?.find(f => f.id === filter.id);
      return {
        ...filter,
        label: config?.label || filter.id,
        options: config?.options
      };
    }), [filters, searchConfig.filters]
  );

  const handleManualRefresh = useCallback(async () => {
    toast.info("جاري تحديث البيانات...");
    try {
      await refetch();
      toast.success("تم تحديث البيانات بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث البيانات");
    }
  }, [refetch]);

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <LoadingSpinnerWithText text="جاري تحميل الشركات..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-center items-center h-64 flex-col space-y-4">
          <div className="text-lg text-destructive">حدث خطأ في تحميل البيانات</div>
          <Button onClick={handleManualRefresh} variant="outline">
            <RefreshCw className="w-4 h-4 ml-2" />
            إعادة المحاولة
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold text-card-foreground">الشركات</h1>
        
        {/* زر الإضافة */}
        <Button onClick={handleAddCompany} className="gap-2">
          <Plus className="w-4 h-4" />
          إضافة شركة
        </Button>
        
        {/* تصدير البيانات */}
        <DataExport
          data={filteredCompanies}
          columns={[
            { key: 'name_ar', title: 'الاسم العربي', dataIndex: 'name_ar' },
            { key: 'name_en', title: 'الاسم الإنجليزي', dataIndex: 'name_en' },
            { key: 'services', title: 'الخدمات', dataIndex: 'services' },
            { key: 'status', title: 'الحالة', dataIndex: 'status' }
          ]}
          filename="الشركات"
        />
        
        {/* تبديل العرض */}
        <ViewToggle view={view} onViewChange={setView} />
        
        {/* البحث السريع */}
        <div className="relative flex-1 max-w-md mr-auto">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={searchConfig.placeholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-10"
          />
        </div>
      </div>

      {/* البحث المتقدم */}
      <AdvancedSearch
        config={searchConfig}
        onSearch={handleSearch}
        initialQuery={searchQuery}
        initialFilters={searchFilters}
      />

      {/* الجدول المحسن */}
      <OptimizedTable
        columns={tableColumns}
        data={displayCompanies}
        loading={isLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: stats.filtered,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100]
        }}
        onPaginationChange={changePage}
        sortConfig={sortConfig ? { key: sortConfig.key as string, direction: sortConfig.direction } : undefined}
        onSortChange={(config) => config ? changeSort(config.key as any, config.direction) : changeSort('id')}
        rowKey="id"
        emptyText="لا يوجد شركات"
        size="middle"
      />

      {/* مربع حوار الشركة */}
      <CompanyDialog 
        company={selectedCompany}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSuccess={() => {
          setSelectedCompany(undefined);
          setIsDialogOpen(false);
          refetch();
        }}
      />
    </div>
  );
};

export default Companies;
